import React, { useState, useEffect } from 'react';
import AwardsStrip from '../components/AwardsStrip';
import { Link, useParams, Navigate } from 'react-router-dom';
import { API } from '../lib/api';
import { PRIMARY_SUBURB_NAMES } from '../lib/suburbs';
import { useSeo, localBusinessLd, BIZ, trackEvent } from '../lib/seo';

const SEO_TITLES = {
  fortnightly: 'Fortnightly Home Cleaning Moreton Bay | The T Touch',
  bond: 'Bond & Exit Cleaning Moreton Bay | The T Touch',
  deep: 'Deep & Reset Cleaning Moreton Bay | The T Touch',
};

const ICONS = (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
    <symbol id="i-check" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></symbol>
    <symbol id="i-shield" viewBox="0 0 24 24"><path d="M12 3l8 3v5c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-3z" /><path d="M9 12l2 2 4-4" /></symbol>
    <symbol id="i-user" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" /></symbol>
    <symbol id="i-phone" viewBox="0 0 24 24"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z" /></symbol>
    <symbol id="i-arrow" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></symbol>
  </svg>
);

const OFFERS = {
  fortnightly: {
    service: 'Recurring Home Care',
    eyebrow: 'Fortnightly home cleaning · Moreton Bay',
    h1: 'A spotless home, every fortnight — without lifting a finger.',
    lead: 'The same trusted cleaner every visit, finished to the T Touch Standard. You’re not paying for a clean house — you’re buying back your weekends.',
    price: '165', unit: '/ visit', priceNote: 'from · incl. GST',
    hero: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=720&h=900&fit=crop&q=72&auto=format',
    bullets: ['The same cleaner who learns your home', '40-point room-by-room standard', 'Free re-clean within 24 hours if it’s not right', 'No lock-in — pause or stop anytime'],
  },
  bond: {
    service: 'Bond / Exit Clean',
    eyebrow: 'Bond & exit cleaning · Moreton Bay',
    h1: 'Bond cleans that pass inspection — the first time.',
    lead: 'Moving out is stressful enough. We deliver an inspection-ready clean backed by our bond warranty, with before-and-after photos for your agent.',
    price: '460', unit: '', priceNote: 'from · incl. GST',
    hero: 'https://images.unsplash.com/photo-1722650272764-08d92d193a9c?w=720&h=900&fit=crop&q=72&auto=format',
    bullets: ['Cleaned to the agent’s exit checklist', 'Bond-back warranty — we return free if flagged', 'Before-and-after photos + tax invoice', 'Fully insured & police-checked'],
  },
  deep: {
    service: 'Deep / Reset Clean',
    eyebrow: 'Deep & reset cleaning · Moreton Bay',
    h1: 'A top-to-bottom reset that makes your home feel new again.',
    lead: 'Perfect before starting a recurring plan or for a seasonal refresh. Every surface, corner and detail — brought back to its best.',
    price: '380', unit: '', priceNote: 'from · incl. GST',
    hero: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=720&h=900&fit=crop&q=72&auto=format',
    bullets: ['Detailed, room-by-room deep clean', 'Kitchen & bathrooms brought back to shine', 'Skirtings, sills, doors & high-touch detailing', 'The perfect start to a recurring plan'],
  },
};

export default function AdLanding() {
  const { variant } = useParams();
  const offer = OFFERS[variant];
  const [reviews, setReviews] = useState([]);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', suburb: '', beds: '', message: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { fetch(`${API}/reviews`).then((r) => r.json()).then(setReviews).catch(() => {}); }, []);

  useSeo({
    title: SEO_TITLES[variant] || 'Home Cleaning Moreton Bay | The T Touch',
    description: offer ? offer.lead : 'Premium home cleaning across Moreton Bay — free quote, same trusted cleaner, 72-hour re-clean guarantee.',
    canonical: `${BIZ.url}/lp/${variant}`,
    image: `${BIZ.url}/assets/logo.png`,
    jsonLd: localBusinessLd(),
  });

  if (!offer) return <Navigate to="/" replace />;
  const initials = (n) => (n || '').split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase();

  const submit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/leads/website`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, service: offer.service, source: `ad · ${variant}` }),
      });
    } catch (err) {}
    trackEvent('generate_lead', { form: 'landing_page', service: offer.service, variant });
    setSent(true);
  };

  return (
    <div style={{ background: '#fff' }} className="lp-has-sticky" data-testid={`landing-${variant}`}>
      {ICONS}
      <AwardsStrip />
      <header className="nav" id="nav"><div className="wrap">
        <Link to="/" className="logoimg" aria-label="The T Touch home" style={{ width: 140, height: 58 }}></Link>
        <div className="nav-r">
          <a href="tel:0466955793" className="nav-phone" onClick={() => trackEvent('contact', { method: 'phone', source: 'lp-nav' })}><svg className="ic" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}><use href="#i-phone" /></svg>0466 955 793</a>
          <a href="#quote" className="btn btn-solid" data-testid="landing-book">Get my free quote</a>
        </div>
      </div></header>

      <section className="hero"><div className="wrap">
        <div className="reveal in">
          <span className="eyebrow">{offer.eyebrow}</span>
          <h1>{offer.h1}</h1>
          <p className="lead">{offer.lead}</p>
          <div className="hero-cta">
            <a href="#quote" className="btn btn-solid btn-lg">Get your free quote <svg className="ic ar" viewBox="0 0 24 24"><use href="#i-arrow" /></svg></a>
            <span className="btn btn-out btn-lg" style={{ pointerEvents: 'none' }}>${offer.price}{offer.unit} · {offer.priceNote}</span>
          </div>
          <div className="hero-trust">
            <div><svg className="ic" viewBox="0 0 24 24"><use href="#i-user" /></svg> Same trusted cleaner</div>
            <div><svg className="ic" viewBox="0 0 24 24"><use href="#i-shield" /></svg> Police-checked &amp; insured</div>
            <div><svg className="ic" viewBox="0 0 24 24"><use href="#i-check" /></svg> Re-clean guarantee</div>
            <div className="aw-tpill" data-testid="lp-award-pill"><span className="dot"></span>2026 Awards Finalist</div>
          </div>
        </div>
        <div className="hero-media reveal in">
          <div className="frame"><img src={offer.hero} alt={offer.service} width="720" height="900" fetchPriority="high" decoding="async" /></div>
          <div className="badge"><span className="lg"></span></div>
          {reviews[0] && (
            <div className="rev"><div className="av">{initials(reviews[0].author)}</div><div><div className="s">{'★'.repeat(reviews[0].rating || 5)}</div><p>&ldquo;{(reviews[0].text || '').split('.')[0]}.&rdquo;</p></div></div>
          )}
        </div>
      </div></section>

      <section className="sec cream"><div className="wrap">
        <div className="sec-head center reveal in"><span className="eyebrow c">What you get</span><h2>Everything you need — nothing you don’t.</h2></div>
        <div className="lp-incl reveal in">
          {offer.bullets.map((b, i) => (
            <div className="lp-inc" key={i}><svg className="ic" viewBox="0 0 24 24"><use href="#i-check" /></svg><span>{b}</span></div>
          ))}
        </div>
      </div></section>

      {reviews.length >= 3 ? (
        <section className="sec" id="reviews"><div className="wrap">
          <div className="sec-head center reveal in"><span className="eyebrow c">Loved by local families</span><h2>The kind of clean people tell their friends about.</h2></div>
          <div className="rev-grid">
            {reviews.slice(0, 3).map((r, i) => (
              <div className="rev reveal in" key={i}><div className="s">{'★'.repeat(r.rating || 5)}</div><p>{r.text}</p><div className="who"><div className="av">{initials(r.author)}</div><div><b>{r.author}</b><small>{r.suburb}</small></div></div></div>
            ))}
          </div>
        </div></section>
      ) : (
        <section className="guarantees" data-testid="guarantee-row"><div className="wrap">
          <div className="guarantee-row">
            <span><svg viewBox="0 0 24 24"><use href="#i-shield" /></svg>Police-checked</span>
            <span><svg viewBox="0 0 24 24"><use href="#i-shield" /></svg>Fully insured</span>
            <span><svg viewBox="0 0 24 24"><use href="#i-check" /></svg>72-hour re-clean guarantee</span>
            <span><svg viewBox="0 0 24 24"><use href="#i-check" /></svg>ABN {BIZ.abn}</span>
          </div>
        </div></section>
      )}

      <section className="cta" id="quote"><div className="bg"><img src="https://images.unsplash.com/photo-1632829882891-5047ccc421bc?w=1100&h=620&fit=crop&q=62&auto=format" alt="" loading="lazy" decoding="async" /></div><div className="wrap">
        <div className="reveal in"><span className="eyebrow lt">Free, no-obligation quote</span><h2 style={{ marginTop: 14 }}>Ready to book your {variant === 'bond' ? 'bond clean' : variant === 'deep' ? 'deep clean' : 'fortnightly clean'}?</h2><p className="lead">Tell us about your place and we’ll come back with a clear, fixed price — <b>we reply within 2 business hours</b>.</p>
          <div className="reassure"><div><svg className="ic" viewBox="0 0 24 24"><use href="#i-check" /></svg>No obligation, no pushy follow-ups</div><div><svg className="ic" viewBox="0 0 24 24"><use href="#i-check" /></svg>A real person reads every enquiry</div><div><svg className="ic" viewBox="0 0 24 24"><use href="#i-check" /></svg>Instant branded reply, day or night</div></div>
        </div>
        <div className="qform reveal in">
          {!sent ? (
            <form onSubmit={submit}>
              <h3>Get your free quote</h3><p className="sub">Takes about two minutes.</p>
              <div className="frow"><div className="field"><label>First name</label><input data-testid="lp-name" type="text" placeholder="Your name" value={form.name} onChange={set('name')} required /></div><div className="field"><label>Phone</label><input data-testid="lp-phone" type="tel" placeholder="04xx xxx xxx" value={form.phone} onChange={set('phone')} /></div></div>
              <div className="frow"><div className="field"><label>Email</label><input data-testid="lp-email" type="email" placeholder="you@email.com" value={form.email} onChange={set('email')} /></div><div className="field"><label>Suburb</label><input list="tt-suburbs" autoComplete="off" data-testid="lp-suburb" placeholder="Start typing your suburb…" value={form.suburb} onChange={set('suburb')} /><datalist id="tt-suburbs">{PRIMARY_SUBURB_NAMES.map((s) => <option key={s} value={s} />)}</datalist></div></div>
              <div className="field full" style={{ marginBottom: 16 }}><label>Anything else? (optional)</label><textarea data-testid="lp-message" placeholder="Preferred day, pets, parking, special requests…" value={form.message} onChange={set('message')}></textarea></div>
              <button className="btn btn-solid btn-lg" data-testid="lp-submit" type="submit">Get my free quote <svg className="ic ar" viewBox="0 0 24 24"><use href="#i-arrow" /></svg></button>
              <p className="fine">By sending, you agree to be contacted about your quote. We never share your details.</p>
            </form>
          ) : (
            <div className="qok show" data-testid="lp-success"><div className="circ"><svg className="ic" viewBox="0 0 24 24"><use href="#i-check" /></svg></div><h3>Thank you — that’s the hard part done.</h3><p>You’ll get an instant reply from us now, and Taleeia will be in touch <b>within 2 business hours</b>. Prefer to talk now? Call <b>0466 955 793</b>.</p></div>
          )}
        </div>
      </div></section>

      <footer><div className="foot-main"><div className="wrap">
        <div className="foot-bottom" style={{ borderTop: 'none', paddingTop: 8 }}><span>© {new Date().getFullYear()} The T Touch Cleaning Services · ABN 30 984 107 905</span><span>thettouch.com.au · Police-checked &amp; fully insured</span></div>
      </div></div></footer>

      <a href="tel:0466955793" className="sticky-call" data-testid="lp-sticky-call" onClick={() => trackEvent('contact', { method: 'phone', source: 'lp-sticky' })}>
        <svg viewBox="0 0 24 24"><use href="#i-phone" /></svg> Call now — 0466 955 793
      </a>
    </div>
  );
}
